import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    CircularProgress,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { createSecretWithKBToken, listSecrets, deleteSecret } from "../API/KB/Files";
import { encryptKBItem } from "../App/cryptoUtils";
import { getKB } from "../API/KB/KBAPI";
import { getUrlSecretKeys, getUrlSecretsWithLabels } from "./processUrlSecrets";
import { useConfirm } from "../Components/ConfirmContext";

export function UrlSecretsForm({ kbId }) {
    const [open, setOpen] = useState(false);
    const [secrets, setSecrets] = useState([]);
    const [existingSecrets, setExistingSecrets] = useState([]);
    const [adding, setAdding] = useState(false);
    const [loading, setLoading] = useState(true);
    const [deletingIds, setDeletingIds] = useState(new Set());
    const showConfirm = useConfirm();

    const fetchSecrets = async () => {
        const urlSecretsWithLabels = getUrlSecretsWithLabels();
        const urlKeys = urlSecretsWithLabels.map(item => item.key);

        if (urlKeys.length > 0 && kbId) {
            setLoading(true);
            try {
                // Get all existing secrets
                const allSecrets = await listSecrets();
                const allSecretsList = allSecrets?.data || [];

                // Filter existing secrets that match URL keys
                const matchingExisting = allSecretsList.filter(s => urlKeys.includes(s.name));
                setExistingSecrets(matchingExisting);

                // Create form fields only for secrets that don't exist yet
                const existingNames = matchingExisting.map(s => s.name);
                const newSecretObjects = urlSecretsWithLabels
                    .filter(item => !existingNames.includes(item.key))
                    .map(item => ({
                        name: item.key,
                        label: item.label, // Store the custom label
                        inputType: item.inputType || 'password', // Store the input type
                        value: '',
                        error: ''
                    }));

                setSecrets(newSecretObjects);
                setOpen(true);
            } catch (error) {
                console.error("Error checking existing secrets:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchSecrets();
    }, [kbId]);

    const handleClose = () => {
        setOpen(false);
        // Remove add_secrets from URL
        const url = new URL(window.location);
        url.searchParams.delete('add_secrets');
        window.history.replaceState({}, '', url);
    };

    const updateSecretValue = (index, value) => {
        const updated = [...secrets];
        updated[index] = { ...updated[index], value, error: '' };
        setSecrets(updated);
    };

    const handleDeleteSecret = async (secretName) => {
        const confirmed = await showConfirm({
            title: 'Delete Confirmation',
            content: `Are you sure you want to delete "${secretName}"?`,
        });
        if (!confirmed) return;

        try {
            setDeletingIds(prev => new Set(prev).add(secretName));
            await deleteSecret(secretName);
            await fetchSecrets(); // Refresh the lists
            await getKB(); // Update KB data
        } catch (error) {
            console.error("Error deleting secret:", error);
        } finally {
            setDeletingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(secretName);
                return newSet;
            });
        }
    };

    const handleSubmit = async () => {
        // Validate all fields
        const updatedSecrets = secrets.map(secret => ({
            ...secret,
            error: !secret.value ? 'Value is required' : ''
        }));
        
        const hasErrors = updatedSecrets.some(secret => secret.error);
        if (hasErrors) {
            setSecrets(updatedSecrets);
            return;
        }

        try {
            setAdding(true);

            // Create all secrets
            for (const secret of secrets) {
                await createSecretWithKBToken(secret.name, encryptKBItem(secret.value, kbId));
            }

            // Update KB data to refresh secrets
            await getKB();

            handleClose();

            // Refresh the page after successful creation
            window.location.reload();
        } catch (error) {
            console.error("Error creating secrets:", error);
        } finally {
            setAdding(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>Manage Secrets</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" padding={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box style={{ paddingTop: '10px' }}>
                        {/* Existing secrets section */}
                        {existingSecrets.length > 0 && (
                            <>
                                <Typography variant="subtitle1" gutterBottom>
                                    Existing Secrets:
                                </Typography>
                                <List style={{ marginBottom: '20px' }}>
                                    {existingSecrets.map((secret) => (
                                        <ListItem key={secret.name} style={{ paddingLeft: 0, paddingRight: 0 }}>
                                            <ListItemText primary={secret.name} />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={() => handleDeleteSecret(secret.name)}
                                                    disabled={deletingIds.has(secret.name) || adding}
                                                >
                                                    {deletingIds.has(secret.name) ? (
                                                        <CircularProgress size={24} />
                                                    ) : (
                                                        <DeleteIcon />
                                                    )}
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                                {secrets.length > 0 && <Divider style={{ marginBottom: '20px' }} />}
                            </>
                        )}
                        
                        {/* New secrets section */}
                        {secrets.length > 0 && (
                            <>
                                {existingSecrets.length > 0 && (
                                    <Typography variant="subtitle1" gutterBottom>
                                        Add New Secrets:
                                    </Typography>
                                )}
                                {secrets.map((secret, index) => (
                                    <TextField
                                        key={index}
                                        label={secret.label || secret.name} // Use custom label if available
                                        type={secret.inputType || 'password'} // Use the input type from URL or default to password
                                        value={secret.value}
                                        onChange={(e) => updateSecretValue(index, e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        error={!!secret.error}
                                        helperText={secret.error}
                                        disabled={adding}
                                        autoFocus={index === 0 && existingSecrets.length === 0}
                                    />
                                ))}
                            </>
                        )}
                        
                        {/* Buttons */}
                        <Box display="flex" gap={2} marginTop={2}>
                            {secrets.length > 0 && (
                                <Button
                                    onClick={handleSubmit}
                                    color="primary"
                                    variant="contained"
                                    disabled={adding || secrets.length === 0}
                                    fullWidth
                                    startIcon={adding && <CircularProgress size={20} color="inherit" />}
                                >
                                    {adding ? "Creating..." : "Create"}
                                </Button>
                            )}
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                disabled={adding}
                                fullWidth={secrets.length === 0}
                            >
                                {secrets.length === 0 && existingSecrets.length === 0 ? 'Close' : 'Cancel'}
                            </Button>
                        </Box>
                        
                        {/* Show message if all secrets already exist */}
                        {secrets.length === 0 && existingSecrets.length > 0 && (
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px', textAlign: 'center' }}>
                                All requested secrets already exist. You can delete them above if needed.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}